import React from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    VIEW,
    DEFERRED,
    COMPLETED,
    SUPPOSEDLY_COMPLETED,
} from "const"
import { MtTask } from "server/models"
import {
    StoreType,
    DisplayColumns,

    GetTaskAccess,
    showTaskColumnSettings,
    setTaskPopupMenuTarget,
    setTaskPopupMenuShow,
    setTaskActive,
    setTaskSelected,
    addModal,
} from "client/store"
import {
    Link,
    FireIcon,
    UserIcon,
    DateFormatter,
    Deadline,
    TagEditor,
    TableColumn,
    SettingsButton,
    TableMenu,
} from "client/components"
import {
    TitleWrapper,
    PendingReviewWrapper,
    DeferredWrapper,
} from "./styled"


const Columns = () => {
    const baseRoute = useSelector((state: StoreType) => state.task.baseRoute)
    const onSelectTask = useSelector((state: StoreType) => state.task.onSelect)
    const selected = useSelector((state: StoreType) => state.task.selected)

    const taskParticipantList = useSelector((state: StoreType) => state.taskParticipant.taskParticipantList)
    const taskObserverList = useSelector((state: StoreType) => state.taskObserver.taskObserverList)

    const taskColumns = useSelector((state: StoreType) => state.system.userSettings.displayColumns.task)

    const userList = useSelector((state: StoreType) => state.user.userList)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const displayColumns: DisplayColumns = {
        id: taskColumns.id !== undefined ? taskColumns.id : true,
        name: taskColumns.name !== undefined ? taskColumns.name : true,
        deadline: taskColumns.deadline !== undefined ? taskColumns.deadline : true,
        creator: taskColumns.creator !== undefined ? taskColumns.creator : true,
        responsible: taskColumns.responsible !== undefined ? taskColumns.responsible : true,
        status: taskColumns.status !== undefined ? taskColumns.status : true,
        tags: taskColumns.tags !== undefined ? taskColumns.tags : true,
        createdAt: taskColumns.createdAt !== undefined ? taskColumns.createdAt : true,
        updatedAt: taskColumns.updatedAt !== undefined ? taskColumns.updatedAt : true,
        delete: taskColumns.delete !== undefined ? taskColumns.delete : true,
    }

    const taskLink = (task: MtTask, linkContent: any) => {
        const href = `${baseRoute}/${VIEW}/${task.id}`
        return (
            <Link
                href={href}
                onClick={() => dispatch(addModal({ url: href }))}
            >
                {linkContent}
            </Link>
        )
    }

    const showColumnSettings = () => {
        dispatch(showTaskColumnSettings(true))
    }

    const showContextMenu = (
        e: React.MouseEvent,
        active: MtTask,
    ) => {
        e.preventDefault()
        e.stopPropagation()

        dispatch(setTaskPopupMenuTarget(e.currentTarget as HTMLElement))
        dispatch(setTaskPopupMenuShow(true))
        dispatch(setTaskActive(active))
    }

    const selectTask = (task: MtTask) => {
        if (!onSelectTask) return
        let selectedCopy
        if (selected && selected.includes(task.id)) {
            selectedCopy = selected.filter((id) => (id !== task.id))
        } else {
            selectedCopy = selected ? selected.slice() : []
            selectedCopy.push(task.id)
        }
        dispatch(setTaskSelected(selectedCopy))
    }

    const columns: TableColumn[] = [
        {
            field: "id",
            header: lang.id,
            sortable: true,
            display: displayColumns.id,
            onClick: selectTask,
        },
        {
            field: "name",
            header: lang.name,
            sortable: true,
            collapsible: true,
            display: displayColumns.name,
            cellMinWidth: "200px",
            cellMaxWidth: "300px",
            formatter: (task: MtTask) => (
                <TitleWrapper status={task.status}>
                    { onSelectTask ? task.name : taskLink(task, task.name) }
                    {
                        task.highPriority
                        && <FireIcon active={task.highPriority} />
                    }
                </TitleWrapper>
            ),
            onClick: selectTask,
        },
        {
            field: "deadline",
            header: lang.deadline,
            sortable: true,
            display: displayColumns.deadline,
            cellWidth: "200px",
            cellMinWidth: "200px",
            formatter: (task: MtTask) => {
                if (task.status === COMPLETED) return <DateFormatter date={task.deadline} />
                if (task.status === SUPPOSEDLY_COMPLETED) return <PendingReviewWrapper>{lang.pendingReview}</PendingReviewWrapper>
                if (task.status === DEFERRED) return <DeferredWrapper>{lang.deferred}</DeferredWrapper>
                return <Deadline deadline={task.deadline} />
            },
            onClick: selectTask,
        },
        {
            field: "creator",
            header: lang.createdBy,
            display: displayColumns.creator,
            cellMinWidth: "150px",
            formatter: (task: MtTask) => {
                const user = userList[task.creator]
                return (
                    <UserIcon
                        user={user}
                        size="sm"
                        projectId={task.projectId}
                        showProjectStatus
                    />
                )
            },
            onClick: selectTask,
        },
        {
            field: "responsible",
            header: lang.responsible,
            display: displayColumns.responsible,
            cellMinWidth: "150px",
            formatter: (task: MtTask) => {
                const user = userList[task.responsible]
                return (
                    <UserIcon
                        user={user}
                        size="sm"
                        projectId={task.projectId}
                        showProjectStatus
                    />
                )
            },
            onClick: selectTask,
        },
        {
            field: "status",
            header: lang.status,
            display: displayColumns.status,
            cellMinWidth: "150px",
            formatter: (task: MtTask) => lang[task.status],
            onClick: selectTask,
        },
        {
            field: "tags",
            header: lang.tags,
            sortable: true,
            display: displayColumns.tags,
            cellMinWidth: "250px",
            cellMaxWidth: "400px",
            formatter: (task: MtTask) => {
                const selectedTags: number[] = []
                if (task.tags) {
                    task.tags.forEach((row) => {
                        selectedTags.push(row.tagId)
                    })
                }
                return (
                    <TagEditor
                        selectedIds={selectedTags}
                        readOnly
                        tableView
                    />
                )
            },
            onClick: selectTask,
        },
        {
            field: "createdAt",
            header: lang.created,
            sortable: true,
            display: displayColumns.createdAt,
            formatter: (task: MtTask) => <DateFormatter date={task.createdAt} />,
            onClick: selectTask,
        },
        {
            field: "updatedAt",
            header: lang.modified,
            sortable: true,
            display: displayColumns.updatedAt,
            formatter: (task: MtTask) => <DateFormatter date={task.updatedAt} />,
            onClick: selectTask,
        },
    ]

    if (!onSelectTask) {
        columns.unshift({
            field: "menu",
            headerFormatter: () => <SettingsButton onClick={showColumnSettings} />,
            formatter: (task: MtTask) => <TableMenu onClick={(e) => showContextMenu(e, task)} />,
        })
        columns.push({
            field: "delete",
            centerAlign: true,
            deleteAccess: (task: MtTask) => {
                const { allowedToDelete } = GetTaskAccess(task, taskParticipantList, taskObserverList, userData)
                return allowedToDelete
            },
        })
    }

    return { columns, displayColumns }
}
export default Columns
